import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useAnimationControls } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Wrench,
  ArrowRight,
  CheckCircle,
  Zap,
  Brain,
  BarChart3,
  Activity,
  AlertTriangle,
  Play,
  TrendingDown,
  Gauge,
  Target,
  Network,
  Eye,
  Award,
  Settings,
  Radio,
  Bell,
  LineChart,
  DollarSign,
  Calendar,
  Quote,
  Star,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { Card, CardContent } from '../../ui/card';

// PredictiveMaintenance Testimonial Component
interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  avatar: string;
  industry: string;
  color: string;
  impact: string;
}

const predictiveMaintenanceTestimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Robert Johnson',
    role: 'VP of Operations',
    company: 'Manufacturing Solutions Inc',
    content: 'Predictive maintenance transformed our plant operations. The AI detected bearing failures 3 weeks before occurrence with 96% accuracy. Unplanned downtime decreased 72%, maintenance costs reduced 45%, and equipment lifespan extended 28%. ROI achieved in 8 months.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=faces',
    industry: 'Manufacturing',
    color: '#FF6B35',
    impact: '72% less downtime'
  },
  {
    id: 2,
    name: 'Sarah Martinez',
    role: 'Director of Facilities',
    company: 'Energy Grid Systems',
    content: 'Monitoring 450+ critical assets across 12 power stations became seamless. The platform predicted transformer failures 45 days in advance, prevented 34 catastrophic failures, and optimized maintenance schedules. Annual savings exceeded $3.2M. Game-changing technology.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=faces',
    industry: 'Energy',
    color: '#00CFFF',
    impact: '$3.2M saved annually'
  },
  {
    id: 3,
    name: 'Michael Chen',
    role: 'Chief Engineer',
    company: 'Aviation Maintenance Corp',
    content: 'Aircraft engine monitoring with vibration analysis and thermal imaging detected anomalies 94% accurately. Maintenance windows optimized around flight schedules, component replacements reduced 38%, and safety compliance improved to 99.8%. Outstanding reliability platform.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces',
    industry: 'Aviation',
    color: '#0FFFA0',
    impact: '94% accuracy'
  },
  {
    id: 4,
    name: 'Jennifer Williams',
    role: 'Operations Manager',
    company: 'Food Processing Industries',
    content: 'Real-time condition monitoring prevented 67 equipment failures across production lines. Predictive alerts enabled just-in-time parts ordering, inventory costs decreased 52%, and production efficiency increased 31%. The platform paid for itself within 6 months.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=faces',
    industry: 'Food Processing',
    color: '#8A3FFC',
    impact: '31% efficiency gain'
  }
];

const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      className="glass-strong w-[380px] h-full group hover:scale-[1.02] transition-all duration-300 relative flex-shrink-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="absolute top-0 left-0 right-0 h-1 transition-all duration-300"
        style={{ backgroundColor: testimonial.color }}
      />
      
      <motion.div
        className="absolute inset-0 opacity-0 transition-opacity duration-500 blur-xl pointer-events-none"
        animate={{ opacity: isHovered ? 1 : 0 }}
        style={{
          background: `radial-gradient(circle at top, ${testimonial.color}30, transparent 70%)`
        }}
      />

      <CardContent className="p-6 pb-8 relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full overflow-hidden ring-2 transition-all duration-300"
              style={{ borderColor: isHovered ? testimonial.color : 'rgba(255,255,255,0.2)' }}
            >
              <img
                src={testimonial.avatar}
                alt={testimonial.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h4 className="font-bold">{testimonial.name}</h4>
              <p className="text-xs text-muted-foreground">{testimonial.role}</p>
            </div>
          </div>
          <motion.div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${testimonial.color}20` }}
            animate={{ rotate: isHovered ? 360 : 0 }}
            transition={{ duration: 0.6 }}
          >
            <Quote className="w-4 h-4" style={{ color: testimonial.color }} />
          </motion.div>
        </div>

        <div className="mb-4">
          <p className="font-semibold text-sm">{testimonial.company}</p>
          <span
            className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: `${testimonial.color}20`,
              color: testimonial.color
            }}
          >
            {testimonial.industry}
          </span>
        </div>

        <div className="flex gap-1 mb-4">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star
              key={i}
              className="w-4 h-4 fill-current"
              style={{ color: testimonial.color }}
            />
          ))}
        </div>

        <blockquote className="text-sm text-muted-foreground mb-4 flex-grow text-justify">
          "{testimonial.content}"
        </blockquote>

        <div className="flex items-center gap-2 pt-4 border-t border-white/10">
          <TrendingUp className="w-4 h-4" style={{ color: testimonial.color }} />
          <span className="text-xs font-semibold" style={{ color: testimonial.color }}>
            {testimonial.impact}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const MarqueeRow: React.FC<{
  testimonials: Testimonial[];
  direction?: 'left' | 'right';
  speed?: number;
}> = ({ testimonials, direction = 'left', speed = 40 }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const x = useMotionValue(0);
  const controls = useAnimationControls();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isMobile, isPaused, testimonials.length]);

  const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];
  const totalDistance = testimonials.length * 386 * 2;

  useEffect(() => {
    if (isMobile) return;
    
    const startAnimation = async () => {
      const currentX = x.get();
      const targetX = direction === 'left' ? -totalDistance : 0;
      const startX = direction === 'left' ? 0 : -totalDistance;
      
      let progress: number;
      if (direction === 'left') {
        progress = Math.abs(currentX) / totalDistance;
      } else {
        progress = (totalDistance - Math.abs(currentX)) / totalDistance;
      }
      
      progress = progress % 1;
      const remainingTime = speed * (1 - progress);
      
      if (Math.abs(currentX) < 10 || progress > 0.99) {
        x.set(startX);
        await controls.start({
          x: targetX,
          transition: {
            duration: speed,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          },
        });
      } else {
        await controls.start({
          x: targetX,
          transition: {
            duration: remainingTime,
            ease: 'linear',
          },
        });
        
        x.set(startX);
        await controls.start({
          x: targetX,
          transition: {
            duration: speed,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          },
        });
      }
    };

    if (!isPaused) {
      startAnimation();
    } else {
      controls.stop();
    }

    return () => {
      controls.stop();
    };
  }, [isPaused, controls, x, speed, direction, totalDistance, isMobile]);

  if (isMobile) {
    return (
      <div className="relative px-4">
        <div className="flex justify-center items-center">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-[380px]"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="h-[380px]">
              <TestimonialCard testimonial={testimonials[currentIndex]} />
            </div>
          </motion.div>
        </div>
        
        <div className="flex justify-center gap-2 mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-orange-500 w-8'
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden py-3"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div
        className="flex gap-6"
        drag="x"
        dragConstraints={{ left: -4000, right: 100 }}
        dragElastic={0.1}
        dragMomentum={false}
        style={{ 
          x,
          cursor: 'grab',
          userSelect: 'none'
        }}
        animate={controls}
        onDragStart={() => setIsPaused(true)}
        onDragEnd={() => {
          setTimeout(() => setIsPaused(false), 500);
        }}
        whileTap={{ cursor: 'grabbing' }}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <div key={`${testimonial.id}-${index}`} className="h-[380px]">
            <TestimonialCard testimonial={testimonial} />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const PredictiveMaintenanceTestimonials: React.FC = () => {
  return (
    <section className="py-15 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
      
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl pointer-events-none"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.5, 0.3, 0.5],
        }}
        transition={{ duration: 8, repeat: Infinity, delay: 1 }}
      />
      
      <div className="max-w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-10 px-4"
        >
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-orange-500 mr-3 animate-pulse" />
            <h2 className="mb-0">
              <span className="bg-gradient-to-r from-orange-500 via-red-500 to-orange-600 bg-clip-text text-transparent">
                Maintenance Excellence
              </span>{' '}
              Stories
            </h2>
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            See how organizations prevented failures with AI-powered predictive maintenance
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="space-y-0"
        >
          <MarqueeRow testimonials={predictiveMaintenanceTestimonials} direction="left" speed={65} />
        </motion.div>

        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent pointer-events-none z-20" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent pointer-events-none z-20" />
      </div>
    </section>
  );
};

const PredictiveMaintenance: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Brain,
      title: 'Continuous Visual Monitoring',
      description: 'Industrial-grade cameras and edge devices capture live video streams from key equipment. AI models analyze every frame to spot vibration anomalies, irregular movement, loose belts, smoke, or other visual indicators of trouble.'
    },
    {
      icon: Radio,
      title: 'Oil & Fluid Leak Detection',
      description: 'Specialized models recognize sheen, pooling, and dripping patterns around pumps, valves, flanges, and hydraulic lines. When a leak is identified, the system saves annotated images, timestamps the event, and sends targeted alerts.'
    },
    {
      icon: AlertTriangle,
      title: 'Asset Recognition & Location Mapping',
      description: 'Each monitored machine is identified and tagged in the system, with its position mapped to your plant layout. Alerts are precise (“Hydraulic press HP-07 – suspected leak”) so technicians know exactly where to go.'
    },
    {
      icon: BarChart3,
      title: 'Predictive Failure Analytics',
      description: 'Visual signals are combined with historical incidents—and optionally vibration, temperature, or SCADA data—to estimate failure risk and remaining time-to-intervention, giving planners an early head start.'
    },
    {
      icon: Calendar,
      title: 'Unified Dashboard & Workflow Integration',
      description: 'A browser-based and mobile dashboard shows live video tiles, asset health status, and incident timelines. Alerts can automatically create work orders in CMMS/EAM tools and notify teams via email, Teams, or Slack.'
    },
    {
      icon: LineChart,
      title: 'Asset Performance',
      description: 'Comprehensive analytics on equipment efficiency, downtime trends, MTBF/MTTR metrics, and lifecycle cost analysis.'
    }
  ];

  const solutions = [
    {
      icon: Activity,
      title: 'Early Mechanical Failure Detection',
      description: 'The system watches rotating equipment such as motors, fans, and conveyors for abnormal movement, misalignment, or excessive vibration. Deviations from baseline behavior trigger warnings days or weeks before a breakdown.',
      features: ['FFT Analysis', 'Bearing Diagnostics', 'Imbalance Detection', 'Resonance Monitoring', 'Trend Analysis']
    },
    {
      icon: Gauge,
      title: 'Hydraulic & Lubrication Leak Monitoring',
      description: 'Around presses, injection machines, and hydraulic power packs, even small leaks can lead to safety hazards and lost production. Computer vision detects the first visible signs of leakage, enabling quick, targeted repairs.',
      features: ['Hot Spot Detection', 'Thermal Trends', 'Comparative Analysis', 'Alert Thresholds', 'Predictive Cooling']
    },
    {
      icon: Zap,
      title: 'Conveyor & Material Handling Oversight',
      description: 'Cameras along conveyors detect belt mis-tracking, sagging, material spillage, or blocked chutes. Teams can intervene before a minor issue escalates into a full line stoppage or equipment damage.',
      features: ['Power Quality', 'Load Monitoring', 'Efficiency Tracking', 'Consumption Trends', 'Fault Detection']
    },
    {
      icon: Settings,
      title: 'Remote & Unmanned Facility Surveillance',
      description: 'Pump stations, tank farms, and remote assets can be monitored without placing staff on-site around the clock. The platform acts as a digital operator, watching for leaks, abnormal movement, and unsafe situations and escalating incidents instantly.',
      features: ['Runtime Tracking', 'Cycle Counting', 'Pressure Monitoring', 'Flow Analysis', 'Performance KPIs']
    },
    {
      icon: CheckCircle,
      title: 'Extended Use in Quality & Process Control',
      description: 'Once cameras are in place, the same visual infrastructure can be used to spot surface defects, incorrect labeling, or missing components, extending the return on investment beyond maintenance alone.',
    }
  ];

  const compliances = [
    {
      icon: Brain,
      title: 'Secure Transport & Storage',
      description: 'Video streams and event data are encrypted in transit and at rest. Only relevant clips, frames, and metadata are retained, optimizing storage and reducing exposure.',
    },
    {
      icon: Bell,
      title: '	Role-Based Access & Audit Trails',
      description: 'Different teams—maintenance, operations, safety, and management—get role-specific access. Every login, configuration change, and alert action is logged for traceability and audit support.',
    },
    {
      icon: Network,
      title: '	Flexible Deployment Models',
      description: 'The solution can run on-premises, in a private cloud, or in a hybrid configuration, aligning with corporate IT and cybersecurity policies and any sector-specific regulations.',
    },
    {
      icon: Award,
      title: '	Retention, Privacy & Anonymization',
      description: 'Retention periods are configurable by site or asset. Where people appear in the frame, optional blurring or privacy filters can be applied to meet internal policies and privacy rules while preserving technical signals.',
    },
    {
      icon: Star,
      title: '	Integration with Security & Governance Tools',
      description: 'The platform connects to existing identity providers (SSO/LDAP), SIEM monitoring, and incident-management systems so visual maintenance data becomes part of the broader governance and security ecosystem.',
    }
  ];

  const benefits = [
    {
      icon: TrendingDown,
      title: '	Less Unplanned Downtime',
      description: 'Early detection of abnormal behavior and leaks allows teams to plan interventions instead of reacting to sudden failures. Production schedules become more reliable, and costly “all hands” breakdown events are reduced.',
    },
    {
      icon: DollarSign,
      title: '	Lower Maintenance & Clean-Up Costs',
      description: 'Fixing small issues before they cascade into major damage means fewer emergency call-outs, less scrap, and reduced cleaning and environmental remediation after oil or fluid spills.',
    },
    {
      icon: Target,
      title: '	Safer, Cleaner Work Environment',
      description: 'Detecting leaks and unsafe machine behavior helps reduce slip hazards, fires, and environmental incidents. Safety and environmental KPIs improve, and plants are better prepared for inspections.',
    },
    {
      icon: Eye,
      title: '	Better Utilization of Skilled Staff',
      description: 'Technicians no longer need to spend hours on routine visual rounds. They rely on the platform for continuous monitoring and focus their expertise on root-cause analysis and long-term reliability improvements.',
    },
    {
      icon: Calendar,
      title: 'Data-Driven Reliability Strategy',
      description: 'Accumulated visual data and incident history give reliability engineers a richer understanding of how failures develop. This evidence supports smarter maintenance plans, design upgrades, and capex justifications.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-red-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 neon-glow">
                  <Wrench className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-red-400 uppercase tracking-wide">IoT-Based Predictive Analytics</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-red-500 to-orange-600 bg-clip-text text-transparent">Predictive Maintenance</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">AI-Powered Equipment Health</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               This solution uses computer vision to watch critical industrial assets in real time and predict failures before they shut your line down. Fixed or mobile cameras constantly monitor machines, pumps, gearboxes, hydraulic systems, and pipework, detecting abnormal motion, overheating indicators, and early signs of oil or fluid leakage. Instead of relying only on periodic inspections or a few point sensors, the platform gives you a continuous visual “control room” for every visible asset. It learns what normal operation looks like, compares that pattern with live video, and triggers alerts when something starts to drift out of tolerance.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-red-500 to-orange-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-red-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-500">50%</div>
                  <div className="text-sm text-muted-foreground">Less Downtime</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">90%</div>
                  <div className="text-sm text-muted-foreground">Accuracy</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">Monitoring</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-red-500 via-orange-500 to-red-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/preductiveMaintenance.png"
                    alt="Predictive Maintenance IoT Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Advanced IoT and AI technology for equipment health
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Monitoring Capabilities Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">Solutions</span>
            </h2>

          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{solution.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent"> Compliance</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-red-500/50 hover:border-red-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-red-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{compliance.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{compliance.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Measurable operational improvements
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-red-500/50 hover:border-red-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-red-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PredictiveMaintenanceTestimonials />
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-red-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-orange-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Eliminate <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">Unplanned Downtime?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Discover how Predictive Maintenance can reduce equipment failures, extend asset lifecycles, and optimize maintenance operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-red-500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-red-500/25 hover:shadow-red-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-red-500/30 hover:border-red-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PredictiveMaintenance;